import { BookOpenCheck, FileCheck, Files } from 'lucide-react';
import type { SolutionContent } from './types';

export const aziendeSolution: SolutionContent = {
  slug: 'aziende',
  navLabel: 'Aziende',
  navDescription: 'L’ufficio legale che non hai: contratti, dispute e adempimenti.',
  meta: {
    title: 'AI per aziende: norme, contratti e adempimenti',
    description:
      'Verifica contratti, adempimenti e obblighi societari su normativa, prassi e giurisprudenza ufficiali, con i riferimenti collegati ai documenti originali.',
  },
  hero: {
    title: 'Trovanorma per aziende',
    subtitle:
      'Al tuo fianco quando devi verificare un contratto, interpretare una norma, approfondire un adempimento o preparare un’analisi per una decisione aziendale.',
  },
  showcaseAudience: 'aziende',
  queryExamples: [
    {
      title: 'Contratto del fornitore',
      icon: 'FileSearch',
      question:
        'Allego il contratto del nuovo fornitore: clausole critiche, penali nascoste e che cosa rinegozierei prima di firmare.',
      attachment: 'contratto-fornitura.pdf',
      result: 'Revisione con proposte',
    },
    {
      title: 'Cartella esattoriale',
      icon: 'ReceiptText',
      question:
        'Verifica la cartella allegata: vizi di forma, termini e se conviene il ricorso o la rateizzazione.',
      attachment: 'cartella-ader.pdf',
      result: 'Analisi e opzioni',
    },
    {
      title: 'Bandi dalla visura',
      icon: 'Award',
      question:
        'Dalla visura camerale allegata: quali bandi e incentivi sono aperti oggi per un’azienda come la nostra?',
      attachment: 'visura-camerale.pdf',
      result: 'Elenco ragionato',
    },
    {
      title: 'Recupero di un credito',
      icon: 'ReceiptEuro',
      question:
        'Un cliente è moroso da **18.000 €**: prepara sollecito, diffida e la strategia se non paga entro trenta giorni.',
      result: 'Lettere pronte da inviare',
    },
    {
      title: 'Canale whistleblowing',
      icon: 'Megaphone',
      question:
        'Abbiamo **sessanta dipendenti**: dobbiamo attivare il canale di segnalazione interno? Che cosa serve e quali sanzioni rischiamo?',
      result: 'Risposta con le fonti',
    },
    {
      title: 'Licenza d’uso del marchio',
      icon: 'Copyright',
      question:
        'Trasforma la bozza allegata in un contratto di licenza d’uso del marchio completo: oggetto, durata, corrispettivi, recesso e foro competente.',
      attachment: 'bozza-licenza.docx',
      result: 'Contratto in Word',
    },
  ],
  tasks: [
    {
      icon: BookOpenCheck,
      title: 'Quesiti normativi, fiscali e societari',
      description:
        'Approfondisci obblighi, adempimenti e questioni fiscali o societarie con risposte documentate e riferimenti alle fonti utilizzate.',
    },
    {
      icon: FileCheck,
      title: 'Contratti, policy e documenti aziendali',
      description:
        'Carica contratti, policy, comunicazioni e altri documenti per analizzarne contenuti, individuare i punti da approfondire e ricevere proposte di revisione.',
    },
    {
      icon: Files,
      title: 'Analisi per decisioni e consulenza',
      description:
        'Trasforma un quesito o una verifica in un’analisi strutturata da condividere con direzione, colleghi o consulenti esterni.',
    },
  ],
  comparison: {
    title:
      'Non tutte le AI sono costruite per il lavoro normativo, fiscale e societario dell’impresa',
    text: 'Quando una risposta entra in un contratto, in un adempimento o in una decisione aziendale, deve poter essere verificata sulle fonti.',
    genericLabel: 'AI generalista',
    drawbacks: [
      'Riferimenti a norme e prassi non sempre presenti',
      'Aggiornamento delle fonti non garantito',
      'Norme, fiscalità e adempimenti trattati in un contesto generalista',
      'Analisi separate dai documenti aziendali',
      'Risultati da trasformare in documenti condivisibili',
    ],
    strengths: [
      'Riferimenti collegati ai documenti originali',
      'Fonti ufficiali sincronizzate ogni giorno',
      'Normativa, prassi e giurisprudenza messe in relazione con i documenti aziendali',
      'Contratti e allegati analizzati nel contesto della questione',
      'Analisi e documenti già strutturati da condividere',
    ],
  },
  featuresTitle:
    'Le funzionalità più utili nel lavoro normativo, fiscale e societario dell’impresa',
  featuredFeatures: [
    {
      slug: 'analisi-documentale',
      description:
        'Analizza contratti, policy e altri documenti nel contesto della questione su cui stai lavorando.',
    },
    {
      slug: 'ricerca-e-quesiti',
      description:
        'Approfondisci obblighi, adempimenti e questioni fiscali o societarie con riferimenti verificabili.',
    },
    {
      slug: 'pareri-e-memorandum',
      description:
        'Trasforma l’analisi in un documento strutturato da condividere con direzione, colleghi o consulenti esterni.',
    },
  ],
  cta: {
    title: 'Porta Trovanorma nelle decisioni della tua azienda',
    text: 'Dalla verifica di un contratto a un adempimento o una questione fiscale, approfondisci ciò che serve prima di decidere come procedere.',
  },
};
